import { useDatasets } from 'api/dataset/dataset.api'
import {
  ConfigurableProperty,
  DigitalTwin,
  DigitalTwinSettingCreationRequest,
  useDigitalTwinSettings,
} from 'api/digitalTwin/digitalTwin.api'
import type { DeviationSettingValue } from 'api/digitalTwin/digitalTwin.types'
import { BASE_SETTING_PRIO, DEVIATION_SETTING_PRIORITIES } from 'api/digitalTwin/digitalTwinConstants'
import { useUiConfig } from 'api/uiConfig/uiConfig.api'
import { ensureUniformTimeIntervals } from 'helpers/dataset.helper/dataset.helper'
import { isValidRegex } from 'helpers/global.helper/global.helper'
import { DateTimePickerRange } from 'helpers/settingsModal.helper/settingsModal.helper'
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import authStore from 'store/auth/auth'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { Button, Card } from 'ui/atoms'
import NewCheckbox from 'ui/atoms/NewCheckbox/NewCheckbox'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import Dropdown from 'ui/components/Dropdown/Dropdown'
import FormRow from 'ui/components/FormRow/FormRow'
import { NonFormSettingDateTimePicker } from 'ui/components/ObjectPropertyFormSelector/components/ObjectPropertySettingForm/components/SettingDateTimePicker/SettingDateTimePicker'
import Title from 'ui/molecules/Title/Title'
import DeviationSettings from 'ui/uiConfig/anchorComponents/FollowupProduction/DeviationSettings/DeviationSettings'
import Chart from 'ui/uiConfig/components/Chart/Chart'
import { ChartConfigWithZoom, ChartItem } from 'ui/uiConfig/components/Chart/Chart.types'
import { DEFAULT_TIME_SERIES_CONFIG } from 'ui/uiConfig/components/Chart/chart.constants'
import Datetime from 'utils/datetime/datetime'
import getLogger from 'utils/log/log'
import { useSnapshot } from 'valtio'
import { getPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import DisplayEventContainer from 'views/EventsView/components/DisplayEventContainer/DisplayEventContainer'
import { EventChangeRequest, EventPost } from 'views/EventsView/components/events.helper'
import DigitalTwinSettingValueSelect from '../DigitalTwinSettingValueSelect/DigitalTwinSettingValueSelect'
import InfoBanner from '../InfoBanner/InfoBanner'
import { MODIFY_SETTING_COLUMN_RETURN_ID, TIME_SERIES_VISUALIZATION } from './constants'
import DigitalTwinSettingCommentField from './DigitalTwinSettingCommentField'
import { canChangeDeviationType, getDeviationTypeHelpText } from './DigitalTwinSettingForm.helper'
import styles from './DigitalTwinSettingForm.module.less'
import TimeSeriesSettingTable, { getTableKey } from './TimeSeriesSettingTable'
import { useShouldEnableComputableTimeSeriesSettings } from './useShouldEnableComputableTimeSeriesSettings'

const log = getLogger('digitalTwinSettingForm')

/** Whether a setting's value holds hours, as a time series' does; a scalar, empty included, does not. */
function isHoursValue(
  value: DigitalTwinSettingCreationRequest['value']
): value is Exclude<DigitalTwinSettingCreationRequest['value'], number | null | undefined> {
  return value !== null && typeof value === 'object'
}

function getValuesArrayFromSettingValue(
  times: ISODateTime[],
  value?: number | null | { [time: ISODateTime]: { id?: number; value: number | null } }
): { id?: number; value: number | null }[] {
  return times.map((time) => (isHoursValue(value) && time in value ? value[time] : { value: 0 }))
}

type DigitalTwinSettingFormProps = {
  settingData: DigitalTwinSettingCreationRequest
  setSettingData: (data: DigitalTwinSettingCreationRequest) => void
  priorities: SystemSettingPriorityLevel[]
  baseSettingPriorities?: SystemSettingPriorityLevel[]
  startEndTimeRange: DateTimePickerRange
  isUpdate: boolean
  digitalTwin: DigitalTwin
  activeSettingId?: number
  errorHelperText?: string
  timeSeriesRegexFilter?: string
  activeConfigurableProperty?: ConfigurableProperty
  dialogContentRef?: React.RefObject<HTMLDivElement | null>
  invalidValuesIndex?: number[]
  operationalEvent?: EventPost | null | EventChangeRequest
  disableAll?: boolean
  isCollectedSettings?: boolean
}

function getReturnIdRegexFilterForSetting(settingData: DigitalTwinSettingCreationRequest): string {
  const isOverrideDemandForNode = settingData.level === 'node' && settingData.attribute === 'override_demand'
  const regexFilter = isOverrideDemandForNode
    ? `^demands\\..*\\.${settingData.name}.*`
    : `^(.*\\.)?${settingData.name}(\\..*)?$`
  return regexFilter
}

export default function DigitalTwinSettingForm({
  settingData,
  setSettingData,
  priorities,
  baseSettingPriorities,
  startEndTimeRange,
  isUpdate,
  digitalTwin,
  activeSettingId,
  errorHelperText,
  timeSeriesRegexFilter,
  activeConfigurableProperty,
  dialogContentRef,
  invalidValuesIndex,
  operationalEvent,
  disableAll = false,
  isCollectedSettings = false,
}: DigitalTwinSettingFormProps): ReactElement {
  const { activeSystem, systemId: globalSystemId, hasAccess } = useAuth()
  const systemId = activeSystem?.id ?? globalSystemId

  const { t } = useTranslation()

  const hasExcludeSettingAccess = hasAccess({ submodule: 'optimizesettings_exclude' })

  const hasSettingsOutsideTimePeriod = useMemo(() => {
    if (!operationalEvent || !settingData.start_time || !settingData.end_time) {
      return false
    }

    const startsBeforeEvent = Datetime.isBefore(
      Datetime.toISOString(settingData.start_time),
      Datetime.toISOString(operationalEvent.start_time)
    )
    const endsAfterEvent = Datetime.isAfter(
      Datetime.toISOString(settingData.end_time),
      Datetime.toISOString(operationalEvent.end_time)
    )
    return startsBeforeEvent || endsAfterEvent
  }, [operationalEvent, settingData.end_time, settingData.start_time])

  // Used to display the time period the user has selected in the time pickers.
  const [editedTimePeriod, setEditedTimePeriod] = useState<{
    start: ISODateTime | null | undefined
    end: ISODateTime | null | undefined
  }>({
    start: settingData.start_time,
    end: settingData.end_time,
  })

  // Used to keep track of when the edited setting changes.
  // If we switch which setting we edit, we want to restore the time pickers to the start and end time of the new setting.
  const [settingStartAndEnd, setSettingStartAndEnd] = useState<{
    start: ISODateTime | null | undefined
    end: ISODateTime | null | undefined
  }>({
    start: settingData.start_time,
    end: settingData.end_time,
  })

  const times = useMemo(
    () =>
      settingData.isTimeSeries && settingData.start_time && settingData.end_time
        ? Datetime.getHoursBetween(settingData.start_time, settingData.end_time)
        : [],
    [settingData.end_time, settingData.isTimeSeries, settingData.start_time]
  )

  const values = getValuesArrayFromSettingValue(times, settingData.value)
  const [tableKey, setTableKey] = useState<string>(getTableKey(values))

  const startTime = settingData.start_time
  const endTime = settingData.end_time

  // Convert the setting value to a dataset for the chart
  const chartSettingDataSet = useMemo(() => {
    const chartSettingItem: Dataset = {
      return_id: MODIFY_SETTING_COLUMN_RETURN_ID,
      times: [],
      values: [],
    }
    if (settingData.isTimeSeries && isHoursValue(settingData.value)) {
      Object.entries(settingData.value).forEach(([time, { value }]) => {
        ;(chartSettingItem.times as ISODateTime[]).push(time as ISODateTime)
        chartSettingItem.values.push(value)
      })
    }
    return chartSettingItem
  }, [settingData.isTimeSeries, settingData.value])

  const [settingBaseValue, setSettingBaseValue] = useState<DeviationSettingValue | string>()

  const { mutateAsync: fetchDigitalTwinSettings } = useDigitalTwinSettings()
  useEffect(() => {
    if (baseSettingPriorities === undefined || baseSettingPriorities.length === 0) {
      // The current view has no base setting priorities specified, so we just use the model default.
      setSettingBaseValue(activeConfigurableProperty?.default_value)
    } else {
      fetchDigitalTwinSettings({
        activeFrom: settingData.start_time,
        activeTo: settingData.end_time,
        properties: [
          {
            level: settingData.level,
            name: settingData.name,
            attribute: settingData.attribute,
          },
        ],
        priorities: baseSettingPriorities,
      }).then((settings) => {
        if (settings.length > 0) {
          setSettingBaseValue(Array.isArray(settings[0].value) ? settings[0].value[0].value : settings[0].value)
        } else {
          setSettingBaseValue(activeConfigurableProperty?.default_value)
        }
      })
    }
  }, [
    activeConfigurableProperty,
    baseSettingPriorities,
    fetchDigitalTwinSettings,
    settingData.attribute,
    settingData.end_time,
    settingData.level,
    settingData.name,
    settingData.start_time,
  ])

  // Use the dataset instructions of the `TIME_SERIES_VISUALIZATION` anchor component to fetch data.
  const authSnap = useSnapshot(authStore)
  const uiConfigTimeSeriesRes = useUiConfig(systemId, TIME_SERIES_VISUALIZATION, {
    mode: authSnap.isBeta ? `beta` : null,
  })
  const tmpTimeSeriesUiConfig = uiConfigTimeSeriesRes?.data?.find((u) => u.component === TIME_SERIES_VISUALIZATION)
  const uiConfigSnap = useSnapshot(uiConfigStore)
  const uiConfig = uiConfigSnap.getParsedUiConfig(tmpTimeSeriesUiConfig?.uid ?? 0, {}, {})

  let regexFilter = getReturnIdRegexFilterForSetting(settingData)

  // Allow regex override of the dataset instructions to show as columns if time series is used and "time_series_regex_filter" is set on configurable_property level
  if (timeSeriesRegexFilter) {
    if (!isValidRegex(timeSeriesRegexFilter)) {
      log.debugging(
        `Invalid "time_series_regex_filter" filter found for '${settingData.level} - ${settingData.name} - ${settingData.attribute}': ${timeSeriesRegexFilter}. Using default regex filter.`
      )
    } else {
      regexFilter = timeSeriesRegexFilter
    }
  }

  const datasetRes = useDatasets(
    uiConfig?.id ?? 0,
    uiConfig?.version ?? 0,
    uiConfig?.dataset_instructions ?? [],
    startTime ?? undefined,
    endTime ?? undefined,
    {
      uid: uiConfig?.uid ?? 0,
      returnIdRegexFilter: regexFilter,
      forceFillMissingHours: true,
    }
  )

  const chartConfig = DEFAULT_TIME_SERIES_CONFIG
  if (chartConfig.options) {
    chartConfig.options.ignoreUpdateDataResolutionOnZoom = true
    chartConfig.options.spanGaps = true
  }

  // Datasets can have different time intervals, we need to ensure they are uniform for the chart.
  // This component was originally written under the assumption that all datasets have whole hour intervals,
  // but this is no longer true since we added support for finer time intervals to support 15-minute
  // electricity market.
  const uniformDatasets = useMemo(() => {
    const fetchedDatasets = datasetRes.data ?? []
    if (fetchedDatasets.length === 0 || fetchedDatasets[0].times.length === 0) {
      return [chartSettingDataSet]
    }

    const earliestFetchedTime = fetchedDatasets[0].times[0] as ISODateTime
    const latestFetchedTime = fetchedDatasets[0].times[fetchedDatasets[0].times.length - 1] as ISODateTime
    return ensureUniformTimeIntervals(
      [...fetchedDatasets, chartSettingDataSet],
      earliestFetchedTime,
      latestFetchedTime,
      true
    )
  }, [datasetRes.data, chartSettingDataSet])

  const setDates = useCallback(
    (startTime: ISODateTime | undefined | null, endTime: ISODateTime | undefined | null) => {
      setSettingData({
        ...settingData,
        start_time: startTime || null,
        end_time: endTime || null,
      })
    },
    [setSettingData, settingData]
  )

  const updateTableKey = useCallback(
    (newStartTime: ISODateTime | undefined | null, newEndTime: ISODateTime | undefined | null) => {
      const newTimes =
        isHoursValue(settingData.value) && newStartTime && newEndTime
          ? Datetime.getHoursBetween(newStartTime, newEndTime)
          : []

      // When start time changes, we have to refresh the table. Otherwise, the start time will be the same
      // and the end time will change. When we refresh the table, all rows are pushed up to their new correct slot.
      const newTableKey = `${newTimes.reduce((v, s) => `${s},${v}`, '')}`
      setTableKey(newTableKey)
    },
    [settingData.value]
  )

  const updateSelectedTimePeriod = useCallback(
    (newStartTime: ISODateTime | undefined | null, newEndTime: ISODateTime | undefined | null) => {
      setDates(newStartTime, newEndTime)
      updateTableKey(newStartTime, newEndTime)
    },
    [setDates, updateTableKey]
  )
  const enableComputableTimeSeriesSettingsFeatureFlag = useShouldEnableComputableTimeSeriesSettings()

  // Should not be shown for 'add_on_demand' settings, since computable settings are not adapted for relative settings
  const enableComputableTimeSeriesSettings =
    enableComputableTimeSeriesSettingsFeatureFlag && activeConfigurableProperty?.attribute !== 'add_to_demand'

  // Check if user has switched which setting is being edited, or if the user is now creating a new setting.
  // If this is the case, update the time pickers to match the new setting.
  useEffect(() => {
    if (settingStartAndEnd.start !== settingData.start_time || settingStartAndEnd.end !== settingData.end_time) {
      setEditedTimePeriod({
        start: settingData.start_time,
        end: settingData.end_time,
      })
      updateTableKey(settingData.start_time, settingData.end_time)
      setSettingStartAndEnd({
        start: settingData.start_time,
        end: settingData.end_time,
      })
    }
  }, [settingData.end_time, settingData.start_time, settingStartAndEnd.end, settingStartAndEnd.start, updateTableKey])

  const timePickerPeriodMatchesSetting =
    editedTimePeriod.start === settingData.start_time && editedTimePeriod.end === settingData.end_time
  const endTimeIsBeforeStartTime = !!(
    editedTimePeriod.start &&
    editedTimePeriod.end &&
    editedTimePeriod.end < editedTimePeriod.start
  )

  const hasWriteFalseProperty = activeConfigurableProperty?.write === false
  const typeEditable = canChangeDeviationType({ isUpdate, isCollectedSettings })

  const modifiedSettingTitle = `${getPrettyName(settingData.name, digitalTwin.translations)}: ${getPrettyName(settingData.attribute, digitalTwin.translations)}`
  const deviationTypeOptions = priorities.map((priority) => {
    const helpText = getDeviationTypeHelpText(priority.priority_level, t)
    return {
      priority_level: priority.priority_level,
      label: (
        <div className={styles.DigitalTwinSettingForm_DeviationTypeOptionRow}>
          <span>{t(priority.display_name) ?? String(priority.priority_level)}</span>
          {helpText && <InfoBanner text={helpText} variant="icon" style="info" />}
        </div>
      ),
      displayText: t(priority.display_name) ?? String(priority.priority_level),
    }
  })

  const renderDeviationTypeValue = (value: number | undefined) => {
    const allPrioritiesLevelTypes = [
      ...deviationTypeOptions,
      { priority_level: BASE_SETTING_PRIO.priority_level, displayText: t(BASE_SETTING_PRIO.display_name) },
    ]
    const option = allPrioritiesLevelTypes.find((opt) => opt.priority_level === value)
    return (
      <div className={styles.DigitalTwinSettingForm_DeviationTypeSelectedValue}>
        <span>{option?.displayText ?? ''}</span>
      </div>
    )
  }
  const availableExcludeSettingOptions = priorities.filter(
    (p) =>
      p.priority_level !== BASE_SETTING_PRIO.priority_level &&
      !DEVIATION_SETTING_PRIORITIES.some((d) => d.priority_level === p.priority_level)
  )

  const canEditProperty = activeConfigurableProperty?.write !== false

  const displayAdvancedSettings =
    isCollectedSettings &&
    hasExcludeSettingAccess &&
    canEditProperty &&
    availableExcludeSettingOptions.length > 0 &&
    DEVIATION_SETTING_PRIORITIES.some((d) => d.priority_level === settingData.priority)

  return (
    <>
      <FormRow spacing={1}>
        <div className={styles.DigitalTwinSettingForm_DatePickers}>
          <NonFormSettingDateTimePicker
            value={{
              start: editedTimePeriod.start,
              end: editedTimePeriod.end,
            }}
            isDisabled={disableAll || (isUpdate && settingData.isTimeSeries) || hasWriteFalseProperty}
            allowEmptyEndDate={!settingData.isTimeSeries}
            minValue={startEndTimeRange?.startTime.min}
            maxValue={startEndTimeRange?.endTime.max}
            setDates={(newStartTime, newEndTime) => {
              setEditedTimePeriod({ start: newStartTime, end: newEndTime })

              // For time series settings we don't update the time period until user presses the 'load period' button.
              // For normal settings we update the time period immediately.
              if (!settingData.isTimeSeries) {
                updateSelectedTimePeriod(newStartTime, newEndTime)
              }
            }}
          />
        </div>
      </FormRow>
      {hasSettingsOutsideTimePeriod && !settingData.isTimeSeries && (
        <FormRow spacing={1} style={{ marginTop: '10px', marginBottom: '10px' }}>
          <InfoBanner text={t(`The time period for this setting is outside the connected event`)} style={'warning'} />
        </FormRow>
      )}
      {settingData.isTimeSeries && (
        <FormRow spacing={1}>
          {isUpdate && !disableAll && (
            <InfoBanner
              style="info"
              text={t(
                'Changes to the period of an existing time series setting is not allowed - create a new setting if you want to make changes'
              )}
            />
          )}

          <div className={styles.DigitalTwinSettingForm_LoadPeriodContainer}>
            <Button
              disabled={disableAll || timePickerPeriodMatchesSetting || endTimeIsBeforeStartTime}
              primary
              data-testid="load-period-button" // e2e: time-series load-period button — do not remove
              onClick={() => {
                updateSelectedTimePeriod(editedTimePeriod.start, editedTimePeriod.end)
              }}
            >
              {t(`Load period`)}
            </Button>
            {!timePickerPeriodMatchesSetting && (
              <InfoBanner variant="text" style="info" text={t('Time has changed, load period to update the table')} />
            )}
            {hasSettingsOutsideTimePeriod && (
              <div style={{ marginLeft: '10px' }}>
                <InfoBanner
                  text={t(`The time period for this setting is outside the connected event`)}
                  style={'warning'}
                />
              </div>
            )}
          </div>
        </FormRow>
      )}
      <FormRow spacing={1}>
        <div className={styles.DigitalTwinSettingForm_DeviationTypeAndValue}>
          <div
            className={`${styles.DigitalTwinSettingForm_DeviationTypeDropdownContainer} ${styles.DigitalTwinSettingForm_CustomWidth}`}
          >
            <div className={styles.DigitalTwinSettingForm_DeviationTypeSelectedValueInfoBanner}>
              <Dropdown
                disabled={disableAll || priorities.length === 1 || !typeEditable}
                fullWidth
                label={t('Deviation type')}
                data-testid="deviation-type-selector" // e2e: deviation type select — do not remove
                items={deviationTypeOptions.map((option) => ({
                  value: option.priority_level,
                  label: option.label,
                }))}
                handleChange={(event): void => {
                  const selectedPriority = Number(event?.target?.value)
                  setSettingData({
                    ...settingData,
                    priority: selectedPriority,
                  })
                }}
                value={settingData.priority}
                renderValue={renderDeviationTypeValue}
              />
              <InfoBanner
                text={t(
                  'Deviation types determine how overlapping settings are prioritized within the same time interval. Higher-priority settings take precedence over lower-priority settings and will be chosen for the optimization. Priority order (starting from lowest): Base settings -> Planned -> Unplanned.'
                )}
                variant="icon"
                style="info"
              />
            </div>
          </div>
          {!settingData.isTimeSeries && !isHoursValue(settingData.value) && (
            <div className={styles.DigitalTwinSettingForm_CustomWidth}>
              <DigitalTwinSettingValueSelect
                attribute={settingData.attribute}
                value={settingData.value ?? undefined}
                disabled={disableAll || hasWriteFalseProperty}
                label={t('Value')}
                settingBaseValue={settingBaseValue}
                required
                onChange={(value) => {
                  setSettingData({
                    ...settingData,
                    value: value === '' ? null : typeof value === 'string' ? Number(value) : value,
                  })
                }}
              />
              <p className={styles.DigitalTwinSettingForm_HelperText}>{errorHelperText}</p>
            </div>
          )}
        </div>
      </FormRow>
      <FormRow>
        <div className={styles.DigitalTwinSettingForm_CommentFieldContainer}>
          <DigitalTwinSettingCommentField
            key={`${settingData.isTimeSeries ? 'time-series' : 'setting'}-${activeSettingId ?? 'new'}`}
            comment={settingData.comment}
            setComment={(val) => {
              if (val !== settingData.comment) {
                setSettingData({
                  ...settingData,
                  comment: val,
                })
              }
            }}
            disabled={disableAll}
            errorHelperText={errorHelperText}
            t={t}
          />
        </div>
      </FormRow>
      {settingData.source && settingData.source !== 'app' && (
        <FormRow spacing={1} style={{ marginTop: '10px', marginBottom: '10px' }}>
          <InfoBanner
            text={t('This setting was created by {{source}}', { source: t(settingData.source) })}
            style={'info'}
          />
        </FormRow>
      )}
      <div className={settingData.operational_event ? styles.DigitalTwinSettingForm_InformationContainer : ''}>
        <div className="flex flex-wrap justify-end gap-4 items-start">
          {displayAdvancedSettings && (
            <AdvancedSettings
              excludeSettingOptions={availableExcludeSettingOptions}
              settingData={settingData}
              setSettingData={setSettingData}
            />
          )}
          {settingData.operational_event && (
            <div className="max-w-full">
              <DisplayEventContainer
                operationalEventId={settingData.operational_event}
                operationalEvent={operationalEvent}
              />
              {hasWriteFalseProperty && (
                <InfoBanner
                  text={t(`Note that you do not have permission to change or update this setting`)}
                  style={'warning'}
                  variant="text"
                />
              )}
            </div>
          )}
          {settingData.start_time && settingData.end_time && (
            <div className="flex min-w-0 max-w-full justify-end">
              <DeviationSettings
                startTime={settingData.start_time}
                endTime={settingData.end_time}
                activeSettingId={activeSettingId}
                specificDigitalTwin={digitalTwin}
                newSetting={settingData}
                activeUnit={settingData.name}
                settingsStringSuffixType="DigitalTwinSettingForm"
                showPreviousAndUpcomingSettings={true}
              />
            </div>
          )}
        </div>
      </div>
      {settingData.isTimeSeries && (
        <div className={styles.DigitalTwinSettingForm_StickyChart}>
          <FormRow spacing={1}>
            <Chart
              legendKey="digitalTwinSettingForm_measValues"
              title={t('Meas values')}
              items={(uiConfig?.props.items ?? []).map((item: UiConfigPropsItem) =>
                item.data_id === MODIFY_SETTING_COLUMN_RETURN_ID
                  ? ({ ...item, title: modifiedSettingTitle } as ChartItem)
                  : (item as ChartItem)
              )}
              config={chartConfig as ChartConfigWithZoom}
              datasets={uniformDatasets}
              digitalTwinTranslations={digitalTwin.translations}
              settingInfo={{ name: settingData.name, attribute: settingData.attribute }}
            />
          </FormRow>
        </div>
      )}
      <FormRow data-testid="time-series-setting-table">
        {settingData.isTimeSeries && (
          <div className={styles.DigitalTwinSettingForm_TimeSeriesSettingTableContainer}>
            <TimeSeriesSettingTable
              chartItems={(uiConfig?.props.items ?? []) as ChartItem[]}
              datasets={uniformDatasets}
              dialogContentRef={dialogContentRef}
              invalidValuesIndex={invalidValuesIndex}
              setSettingData={setSettingData}
              settingsData={[settingData]}
              times={times}
              tableKey={tableKey}
              setTableKey={setTableKey}
              digitalTwinTranslations={digitalTwin.translations}
              enableComputableSettings={enableComputableTimeSeriesSettings}
              disabled={disableAll || hasWriteFalseProperty}
            />
            <div className={styles.DigitalTwinSettingForm_InvalidValuesHelperText}>{errorHelperText}</div>
          </div>
        )}
      </FormRow>
    </>
  )
}

function AdvancedSettings({
  excludeSettingOptions,
  settingData,
  setSettingData,
}: {
  excludeSettingOptions: SystemSettingPriorityLevel[]
  settingData: DigitalTwinSettingCreationRequest
  setSettingData: (data: DigitalTwinSettingCreationRequest) => void
}): ReactElement {
  const { t } = useTranslation()

  const handleExcludeCheckboxChange = useCallback(
    (priorityLevel: number, isChecked: boolean) => {
      setSettingData({
        ...settingData,
        excluded_priority_layers: isChecked
          ? [...(settingData.excluded_priority_layers ?? []), priorityLevel]
          : (settingData.excluded_priority_layers ?? []).filter((p) => p !== priorityLevel),
      })
    },
    [settingData, setSettingData]
  )

  return (
    <div className="min-w-0 basis-full">
      <Card
        style="table"
        title={t('Advanced settings')}
        collapsible
        defaultCollapsed
        rememberCollapsed
        titleSize="small"
      >
        <div className="flex gap-1">
          <Title title={t('Exclude setting')} size="small" marginLeft />
          <InfoBanner
            variant="icon"
            style="info"
            text={t(
              "Excluding settings means that the setting will not be added to the chosen optimization type. E.g. if you check option 'follow-up' the setting will not render in the follow-up optimizing, but will still be rendered in the production optimization."
            )}
          />
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {excludeSettingOptions.map((p) => {
            const isChecked = (settingData.excluded_priority_layers ?? []).includes(p.priority_level)
            return (
              <NewCheckbox
                key={p.priority_level}
                label={t(p.display_name)}
                checked={isChecked}
                onChange={(e) => handleExcludeCheckboxChange(p.priority_level, e.target.checked)}
              />
            )
          })}
        </div>
      </Card>
    </div>
  )
}
